<template lang="pug">
    v-card(v-if="goalsEnabled")
        v-card-title
            v-btn-toggle(
                v-model="goalsByRecurringType"
                dense
                color="primary"
                style="display: block;"
            )
                v-btn(value="daily" v-if="validDailyGoals")
                    span.hidden-sm-and-down Daily
                v-btn(value="weekly" v-if="validWeeklyGoals")
                    span.hidden-sm-and-down Weekly
                v-btn(value="monthly" v-if="validMonthlyGoals")
                    span.hidden-sm-and-down Monthly
                v-btn(value="quarterly" v-if="validQuarterlyGoals")
                    span.hidden-sm-and-down Quarterly
                v-btn(value="yearly" v-if="validYearlyGoals")
                    span.hidden-sm-and-down Yearly

        v-card-text(v-if="!isLoading")
            .goal-items-container(style="overflow-x: auto;")
                .goal-items(id="goal-items" :style="{ '--goal-item-width': goalItemWidth, display: 'flex', alignItems: 'center', gap: '10px'}" v-if="filteredGoals.length > 0")
                    .goal-item(
                        v-for="goal in filteredGoals" :key="goal.id"
                        :class="{ 'important-width': true }"
                        :style="{ flex: '0 0 auto', display: 'flex', alignItems: 'flex-start', border: '1px solid var(--lt-gray)', padding: '10px', borderRadius: '5px', boxSizing: 'border-box' }"
                    )
                        .goal-info(style="flex: 1; display: flex; flex-direction: column; align-items: flex-start;")
                            .goal-title(style="margin-bottom: 5px;")
                                .text-subtitle-1.font-weight-medium.volie-color-blue {{ goal.goal_type | titleize }}
                            .goal-stats.black--text
                                template(v-if="goal.current_value > 0")
                                    .text-h4.font-weight-bold(style="font-size: x-large !important;") {{ goal.current_value | number }}/{{ goal.target_value | number }}
                                template(v-else)
                                    .text-h4.font-weight-bold(style="font-size: x-large !important;") 0/{{ goal.target_value }}

                        .goal-progress(style="flex: 0 0 70px; display: flex; justify-content: center; align-items: center;")
                            v-progress-circular(
                                rotate="90"
                                :size="70"
                                :width="10"
                                :value="goalProgressPercentage(goal)"
                                :color="progressColor(goal)"
                            )
                                span(style="position: absolute; color: black; font-size: large; top: 50%; left: 50%; transform: translate(-50%, -50%);") {{ Math.floor(goalProgressPercentage(goal)) }}%

                .empty-state(v-else-if="goals.length === 0" style="text-align: left;")
                    v-alert(
                        icon="mdi-trophy"
                        prominent
                        text
                        type="info"
                    )
                        span.font-weight-bold Oh no! You don't have any account-level goals set up for this account.
                            v-btn(
                                style="margin-left: 10px;"
                                rounded
                                color="primary"
                                dark
                                :to="{ name: 'goal_templates_index' }"
                            )
                                | Launch goals now

                .tab-selection-state(v-else style="text-align: left; display: flex; justify-content: left;")
                    v-alert(
                        icon="mdi-trophy"
                        prominent
                        text
                        type="info"
                        style="align-self: center; width: 100%; margin-bottom: 0px;"
                    )
                        strong Please select a tab to view your goals.

        div.text-center(v-else)
            v-progress-circular(
                :size="45"
                :width="10"
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 75px; margin-bottom: 75px;"
            )

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState from '@/store';

    // Data
    import { GetGoals } from "@/data";

    export default Vue.extend({
        data() {
            return {
                bus: VolieState.websocketClient().eventBus(),
                goals: [],
                goalsByRecurringType: "daily",
                goalsEnabled: false,
                isLoading: false,
            }
        },
        methods: {
            getGoals() {
                if (!(this.currentUser?.activated_account_id > 0)) {
                    return;
                }

                const options = {
                    ownable_id: this.currentUser.activated_account_id,
                    ownable_type: "account",
                }

                this.isLoading = true;

                GetGoals(options).then((response) => {
                    if (response?.Data) {
                        this.goalsEnabled = response.Data.goals_enabled;
                        if (response?.Data?.goals?.length > 0) {
                            this.goals = response.Data.goals;
                            this.setDefaultTab();
                        }
                    }

                    this.isLoading = false;
                });
            },
            onGoalsUpdate(response) {

                // Load goal and verify
                const goal = response?.Data;
                if (!(goal?.id > 0)) {
                    return;
                }

                // Find index if applicable
                const index = (this.goals || []).findIndex(obj => obj.goal_id === goal.id);
                if (!(index >= 0)) {
                    // TODO Manually parse it into type we want
                    this.goals.push(this.pushLatestGoalUpdate(goal));
                    return;
                }

                const currentGoal = this.goals[index];
                if (!currentGoal) return;

                this.goals.splice(index, 1, { ...currentGoal, ...goal });
            },
            pushLatestGoalUpdate(data) {
                if (!data) {
                    return {};
                }

                return {
                    goal_id:            data.id,
                    account_id:         data.account_id,
                    // organization_id: ,
                    created_at:         data.created_at,
                    current_value:      data.current_value,
                    end_time:           data.end_time,
                    goal_type:          data.goal_type,
                    recurring_type:     data.recurring_type,
                    start_time:         data.start_time,
                    status:             data.status,
                    target_value:       data.target_value,
                    updated_at:         data.updated_at
                };
            },
            goalProgressPercentage(goal) {
                if (goal?.current_value > 0) {
                    return (goal.current_value / goal.target_value) * 100.0;
                } else {
                    return 0;
                }
            },
            init() {
                this.getGoals();
            },
            progressColor(goal) {
                if (this.goalProgressPercentage(goal) > 0 && this.goalProgressPercentage(goal) < 26) {
                    return "error";
                } else if (this.goalProgressPercentage(goal) >= 26 && this.goalProgressPercentage(goal) < 75) {
                    return "warning lighten-2";
                } else if (this.goalProgressPercentage(goal) >= 75 && this.goalProgressPercentage(goal) < 100) {
                    return "primary";
                } else if (this.goalProgressPercentage(goal) >= 100) {
                    return "success";
                } else {
                    return "grey";
                }
            },
            setDefaultTab() {
                const recurringTypes = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
                for (const type of recurringTypes) {
                    const capitalizedRecurringType = type.charAt(0).toUpperCase() + type.slice(1);
                    if (this[`valid${capitalizedRecurringType}Goals`]) {
                        this.goalsByRecurringType = type;
                        break;
                    }
                }
            }
        },
        computed: {
            currentUser() {
                return VolieState.currentUser();
            },
            goalItemWidth() {
                return `${((document?.getElementById('goal-items')?.getBoundingClientRect()?.width || 1290) - 36) / 4}px`;
            },
            filteredGoals() {
                return this.goals?.filter(goal => goal.recurring_type === this.goalsByRecurringType);
            },
            validDailyGoals() {
                return this.goals && this.goals.map(goal => goal.recurring_type).includes('daily');
            },
            validGoals() {
                return this.goals?.length > 0;
            },
            validMonthlyGoals() {
                return this.goals && this.goals.map(goal => goal.recurring_type).includes('monthly');
            },
            validQuarterlyGoals() {
                return this.goals && this.goals.map(goal => goal.recurring_type).includes('quarterly');
            },
            validWeeklyGoals() {
                return this.goals && this.goals.map(goal => goal.recurring_type).includes('weekly');
            },
            validYearlyGoals() {
                return this.goals && this.goals.map(goal => goal.recurring_type).includes('yearly');
            },
        },
        props: {
            sheetHeight: Number, // not used
        },
        components: {
        },
        created() {
            if (!(this.currentUser?.activated_account_id > 0)) {
                return;
            }

            this.bus.subscribe({ type: "broadcast" , channel: { ID: null, Type: "goalUpdate" }}, this.onGoalsUpdate);
            this.init();
        },
        destroyed() {
            this.bus.unsubscribe({ type: "broadcast" , channel: { ID: null, Type: "goalUpdate" }}, this.onGoalsUpdate);
        },
        updated() {
            this.$emit('goal-update');
        },
    });
</script>

<style lang="scss" scoped>
    #app {
        #goal-items {
            --goal-item-width: 200px;
        }

        .important-width {
            width: var(--goal-item-width) !important;
        }
    }
</style>
